/**
 * @file InactivityRedirectModal.tsx
 *
 * @description Shown after a user was redirected due to inactivity. Must be
 * explicitly acknowledged — no Escape, backdrop, or X dismissal — so the
 * session-timeout message is never missed.
 *
 * @module Modals
 */
import { Typography } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { ConfirmDialog } from "../index";

interface InactivityRedirectModalProps {
  open: boolean;
  onAcknowledge: () => void;
  message?: string;
}

const InactivityRedirectModal = ({
  open,
  onAcknowledge,
  message = "You were redirected due to inactivity.",
}: InactivityRedirectModalProps) => {
  return (
    <ConfirmDialog
      open={open}
      blocking
      singleAction
      onClose={onAcknowledge}
      onConfirm={onAcknowledge}
      tone="warning"
      icon={<WarningAmberIcon />}
      title="Session Ended"
      description={message}
      confirmLabel="I Understand"
    >
      <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.6, mt: 1 }}>
        Your editing session was automatically ended to protect your work. Any
        unsaved changes were preserved.
      </Typography>
    </ConfirmDialog>
  );
};

export default InactivityRedirectModal;
